import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ButtonDirective, ButtonLabel} from 'primeng/button';
import {Loading} from '@components/ui/loading/loading';
import {dateToYmd} from '../../managers/field-details-date.util';

/**
 * Satellite date timeline strip: scrollable days, cloud-cover / anomaly dot styling, calendar shortcut.
 */
@Component({
  selector: 'app-field-details-timeline-section',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonDirective, Loading],
  templateUrl: './field-details-timeline-section.component.html',
  styleUrls: ['./field-details-timeline-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldDetailsTimelineSectionComponent {
  private readonly translate = inject(TranslateService);

  @Input({ required: true }) timelineDatesLoading = false;
  @Input({ required: true }) timelineDates: Date[] = [];
  @Input() selectedTimelineDate: Date | null = null;
  @Input({ required: true }) anomaliesByDay!: Set<string>;
  /** Days where GetAllProductsByPoint reports cloudCover > 50% → grey dot. */
  @Input() highCloudCoverByDay: Set<string> = new Set();

  @Output() calendarOpen = new EventEmitter<void>();
  @Output() datePick = new EventEmitter<Date>();

  @ViewChild('timelineViewport') timelineViewport?: ElementRef<HTMLDivElement>;

  trackTimeline = (_: number, d: Date) => dateToYmd(d);

  onCalendarClick(): void {
    this.calendarOpen.emit();
  }

  onDayClick(d: Date): void {
    this.datePick.emit(d);
  }

  timelineScrollPrev(): void {
    const el = this.timelineViewport?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollBy({ left: -Math.max(140, el.clientWidth * 0.45), behavior: 'smooth' });
  }

  timelineScrollNext(): void {
    const el = this.timelineViewport?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollBy({ left: Math.max(140, el.clientWidth * 0.45), behavior: 'smooth' });
  }

  isAnomalyDay(d: Date): boolean {
    return this.anomaliesByDay.has(dateToYmd(d));
  }

  timelineDotClass(d: Date): string {
    const ymd = dateToYmd(d);
    if (this.anomaliesByDay.has(ymd)) {
      return 'fdd-timeline__dot--bad';
    }
    if (this.highCloudCoverByDay.has(ymd)) {
      return 'fdd-timeline__dot--cloud';
    }
    return 'fdd-timeline__dot--ok';
  }

  timelineNodeTitle(d: Date): string {
    const ymd = dateToYmd(d);
    if (this.highCloudCoverByDay.has(ymd)) {
      return this.translate.instant('FIELD_DETAILS_V2.LEGEND_EMPTY');
    }
    return '';
  }

  isSelectedDay(d: Date): boolean {
    return !!(this.selectedTimelineDate && dateToYmd(d) === dateToYmd(this.selectedTimelineDate));
  }
}
